import type { ParsedUrlQuery } from 'querystring';
import { ShopOrderEnum } from '@/api/types/shop.types';
import { QUERY } from '@/constants/common';

const SHOPS_NAV_SEQ_QUERY = '_shopsSeq';

export type ShopsListFilters = {
  search: string;
  categoryId: string | null;
  order: ShopOrderEnum;
};

export function nextShopsListSeed(): number {
  return Math.floor(Math.random() * 1001);
}

function queryFirstString(
  value: string | string[] | undefined
): string | undefined {
  if (value === undefined) return undefined;
  return Array.isArray(value) ? value[0] : value;
}

type ShopSortMapping = {
  sort: string;
  order: ShopOrderEnum;
};
const SHOP_SORT_MAPPINGS: ShopSortMapping[] = [
  { sort: 'default', order: ShopOrderEnum.Default },
  { sort: 'count', order: ShopOrderEnum.ByAdsCount },
  { sort: 'popularity', order: ShopOrderEnum.ByPopularity },
  { sort: 'name', order: ShopOrderEnum.ByNameAsc },
  { sort: 'name-reverse', order: ShopOrderEnum.ByNameDesc },
];
export function mapSortToOrder(sort: string): ShopOrderEnum {
  const mapping = SHOP_SORT_MAPPINGS.find(item => item.sort === sort);
  return mapping?.order ?? ShopOrderEnum.Default;
}

export function mapOrderToSort(order: ShopOrderEnum): string {
  const mapping = SHOP_SORT_MAPPINGS.find(item => item.order === order);
  return mapping?.sort ?? mapOrderToSort(ShopOrderEnum.Default);
}

export function parseShopsFiltersFromQuery(
  query: ParsedUrlQuery
): ShopsListFilters {
  const search = queryFirstString(query[QUERY.q]) ?? '';
  const categoryId = queryFirstString(query[QUERY.categoryId]) ?? null;
  const sort =
    queryFirstString(query[QUERY.shopSort]) ??
    mapOrderToSort(ShopOrderEnum.Default);

  return {
    search,
    categoryId,
    order: mapSortToOrder(sort),
  };
}

export function buildShopsListQuery(
  currentQuery: ParsedUrlQuery,
  filters: ShopsListFilters
): ParsedUrlQuery {
  const nextQuery: ParsedUrlQuery = { ...currentQuery };
  const trimmedSearch = filters.search.trim();
  const sortValue = mapOrderToSort(filters.order);

  if (trimmedSearch) {
    nextQuery[QUERY.q] = trimmedSearch;
  } else {
    delete nextQuery[QUERY.q];
  }

  if (filters.categoryId) {
    nextQuery[QUERY.categoryId] = filters.categoryId;
  } else {
    delete nextQuery[QUERY.categoryId];
  }

  if (filters.order !== ShopOrderEnum.Default) {
    nextQuery[QUERY.shopSort] = sortValue;
  } else {
    delete nextQuery[QUERY.shopSort];
  }

  delete nextQuery[SHOPS_NAV_SEQ_QUERY];

  return nextQuery;
}

export function shopsListFiltersMatchQuery(
  query: ParsedUrlQuery,
  filters: ShopsListFilters
): boolean {
  const fromQuery = parseShopsFiltersFromQuery(query);
  return shopsListFiltersEqual(fromQuery, filters);
}

export function shopsListFiltersEqual(
  previous: ShopsListFilters,
  next: ShopsListFilters
): boolean {
  return (
    previous.search.trim() === next.search.trim() &&
    previous.categoryId === next.categoryId &&
    previous.order === next.order
  );
}
